import * as React from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, cn } from "@finster/ui";
import { ChevronRight, FolderSymlink, Plus, Upload } from "lucide-react";
import { unsupportedChosenFiles, useComposerUploadContext } from "./composerUploads";
import { UserFileSelector } from "./UserFileSelector";

export interface ComposerAttachmentSecondaryPanelOpenChangeOptions {
  /** Defaults to true when an open panel closes. */
  restoreFocus?: boolean;
  /** Reopens the attachment menu after a secondary panel Back action. */
  reopenMenu?: boolean;
}

export interface ComposerAttachmentSecondaryPanelRenderProps {
  open: boolean;
  anchorRef: React.RefObject<HTMLDivElement | null>;
  triggerRef: React.RefObject<HTMLButtonElement | null>;
  onOpenChange: (
    open: boolean,
    options?: ComposerAttachmentSecondaryPanelOpenChangeOptions
  ) => void;
  restoreTriggerFocus: () => void;
}

export interface ComposerAttachmentSecondaryPanel {
  /** Controlled panel visibility owned by the caller. */
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /**
   * Renders a host-neutral secondary surface anchored to the attachment
   * trigger. The picker owns focus restoration when the controlled panel
   * transitions from open to closed.
   */
  render: (props: ComposerAttachmentSecondaryPanelRenderProps) => React.ReactNode;
}

export interface ComposerAttachmentMenuAction {
  id?: string;
  label: string;
  /**
   * Muted secondary text. Toggle rows stack it under the label.
   * Other rows keep it on the same line.
   */
  description?: string;
  icon?: React.ReactNode;
  onSelect?: () => void | Promise<void>;
  disabled?: boolean;
  /** Shows the disclosure affordance used by actions that open another panel. */
  trailingChevron?: boolean;
  /**
   * Menu group. `"top"`, `"middle"`, or `"bottom"` (default).
   */
  placement?: "top" | "middle" | "bottom";
  /**
   * Toggle state. When set, the row is a `menuitemcheckbox` with a switch.
   */
  active?: boolean;
  /**
   * When false, choosing the row leaves the menu open and focus on that row.
   * Other rows close the menu. Defaults to true.
   */
  closeOnSelect?: boolean;
  /**
   * Caller moves focus; do not restore the trigger on close.
   */
  managesFocus?: boolean;
  /** Optional controlled secondary surface associated with this menu action. */
  secondaryPanel?: ComposerAttachmentSecondaryPanel;
}

/**
 * Matches every focusable menu row: plain `menuitem` plus the
 * `menuitemcheckbox` role that toggle actions opt into. Kept as one prefix
 * selector rather than a comma-separated list because jsdom returns list
 * matches grouped by selector instead of in document order, which would make
 * arrow-key order diverge from the browser.
 */
const MENU_ITEM_SELECTOR = '[role^="menuitem"]:not(:disabled)';

export interface ComposerAttachmentPickerProps {
  selectedFileIds: readonly string[];
  onSelectedFileIdsChange: (fileIds: string[]) => void;
  disabled?: boolean;
  /**
   * Session gate from `addinYourFilesUpload`. Off hides Upload files and the
   * file input. Open Your Files stays available.
   */
  uploadEnabled?: boolean;
  /** Additional host-agnostic actions appended after Open Your Files. */
  menuActions?: readonly ComposerAttachmentMenuAction[];
}

/**
 * Shared composer entry point for attachments.
 *
 * Presents a compact attachment menu. Upload files opens the operating-system
 * file dialog. Open Your Files opens the Your Files picker.
 */
export const ComposerAttachmentPicker: React.FC<ComposerAttachmentPickerProps> = ({
  selectedFileIds,
  onSelectedFileIdsChange,
  disabled = false,
  uploadEnabled = false,
  menuActions = [],
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [fileSelectorOpen, setFileSelectorOpen] = useState(false);
  const [refreshYourFilesOnOpen, setRefreshYourFilesOnOpen] = useState(true);
  const menuRootRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerButtonRef = useRef<HTMLButtonElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [yourFilesAccessDenied, setYourFilesAccessDenied] = useState(false);
  const handleYourFilesAccessChange = useCallback((access: "granted" | "denied") => {
    setYourFilesAccessDenied(access === "denied");
  }, []);
  const suppressSecondaryFocusRestoreRef = useRef(false);
  const secondaryPanelOpen = menuActions.some((action) => action.secondaryPanel?.open);
  const { addRejectedFiles, progress } = useComposerUploadContext();
  const wasSecondaryPanelOpenRef = useRef(secondaryPanelOpen);

  const restoreTriggerFocus = useCallback(() => {
    window.setTimeout(() => {
      triggerButtonRef.current?.focus();
    }, 0);
  }, []);

  const closeMenu = useCallback(
    (restoreFocus = false) => {
      setMenuOpen(false);
      if (restoreFocus) {
        restoreTriggerFocus();
      }
    },
    [restoreTriggerFocus]
  );

  const handleMenuKeyDown = useCallback((event: React.KeyboardEvent<HTMLDivElement>) => {
    const enabledItems = Array.from(
      menuRef.current?.querySelectorAll<HTMLButtonElement>(MENU_ITEM_SELECTOR) ?? []
    );
    if (enabledItems.length === 0) return;

    const currentIndex = enabledItems.indexOf(document.activeElement as HTMLButtonElement);
    let nextItem: HTMLButtonElement | undefined;
    switch (event.key) {
      case "ArrowDown":
        nextItem = enabledItems[(currentIndex + 1) % enabledItems.length];
        break;
      case "ArrowUp":
        nextItem = enabledItems[currentIndex <= 0 ? enabledItems.length - 1 : currentIndex - 1];
        break;
      case "Home":
        nextItem = enabledItems[0];
        break;
      case "End":
        nextItem = enabledItems[enabledItems.length - 1];
        break;
      default:
        return;
    }

    event.preventDefault();
    nextItem?.focus();
  }, []);

  const handleMenuActionSelect = useCallback(
    (action: ComposerAttachmentMenuAction) => {
      const staysOpen = action.closeOnSelect === false;
      if (!staysOpen) {
        setMenuOpen(false);
        action.secondaryPanel?.onOpenChange(true);
      }
      const shouldRestoreFocus = !staysOpen && !action.secondaryPanel && !action.managesFocus;
      try {
        const result = action.onSelect?.();
        if (result) {
          void result
            .catch(() => undefined)
            .finally(() => {
              if (shouldRestoreFocus) restoreTriggerFocus();
            });
          return;
        }
      } catch {
        // The action owns its error state; the picker still restores focus.
      }
      if (shouldRestoreFocus) restoreTriggerFocus();
    },
    [restoreTriggerFocus]
  );

  const handleMenuBlur = useCallback(
    (event: React.FocusEvent<HTMLDivElement>) => {
      // Pointer dismissal is owned by the trigger toggle and the outside-click
      // listener. Blur only handles keyboard focus leaving the entire picker.
      const nextFocus = event.relatedTarget as Node | null;
      if (!nextFocus || menuRootRef.current?.contains(nextFocus)) return;
      closeMenu();
    },
    [closeMenu]
  );

  useEffect(() => {
    if (!menuOpen) return undefined;

    menuRef.current?.querySelector<HTMLButtonElement>(MENU_ITEM_SELECTOR)?.focus();

    const handlePointerDown = (event: MouseEvent) => {
      if (!menuRootRef.current?.contains(event.target as Node)) {
        closeMenu();
      }
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        event.preventDefault();
        closeMenu(true);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [closeMenu, menuOpen]);

  useEffect(() => {
    if (wasSecondaryPanelOpenRef.current && !secondaryPanelOpen) {
      if (!suppressSecondaryFocusRestoreRef.current) {
        restoreTriggerFocus();
      }
      suppressSecondaryFocusRestoreRef.current = false;
    }
    wasSecondaryPanelOpenRef.current = secondaryPanelOpen;
  }, [restoreTriggerFocus, secondaryPanelOpen]);

  if (disabled && (menuOpen || fileSelectorOpen)) {
    setMenuOpen(false);
    setFileSelectorOpen(false);
  }
  useEffect(() => {
    if (!disabled) return;
    menuActions.forEach((action) => {
      if (action.secondaryPanel?.open) {
        action.secondaryPanel.onOpenChange(false);
      }
    });
  }, [disabled, menuActions]);

  const handleFileSelectorOpenChange = useCallback((open: boolean) => {
    setFileSelectorOpen(open);
    if (open) {
      setMenuOpen(false);
    }
  }, []);

  const topActions = menuActions.filter((action) => action.placement === "top");
  const middleActions = menuActions.filter((action) => action.placement === "middle");
  const bottomActions = menuActions.filter(
    (action) => action.placement !== "top" && action.placement !== "middle"
  );
  const hasToggleAction = menuActions.some((action) => action.active !== undefined);
  const uploadFilesAction: ComposerAttachmentMenuAction = {
    id: "upload-files",
    label: "Upload files",
    icon: <Upload className="h-4 w-4" aria-hidden />,
    disabled,
    managesFocus: true,
    onSelect: () => {
      if (yourFilesAccessDenied) {
        setRefreshYourFilesOnOpen(false);
        setFileSelectorOpen(true);
        return;
      }
      fileInputRef.current?.click();
    },
  };
  const openYourFilesAction: ComposerAttachmentMenuAction = {
    id: "open-your-files",
    label: "Open Your Files",
    icon: <FolderSymlink className="h-4 w-4" aria-hidden />,
    trailingChevron: true,
    managesFocus: true,
    onSelect: () => {
      setRefreshYourFilesOnOpen(true);
      setFileSelectorOpen(true);
    },
  };

  const renderMenuAction = (
    action: ComposerAttachmentMenuAction,
    keyPrefix: string,
    index: number
  ) => {
    const isToggle = action.active !== undefined;
    const trailing = isToggle ? (
      <span
        aria-hidden
        className={cn(
          "relative ml-auto inline-flex h-5 w-9 shrink-0 self-center rounded-full",
          action.active ? "bg-primary" : "bg-muted-foreground/25"
        )}
      >
        <span
          className={cn(
            "absolute top-0.5 h-4 w-4 rounded-full bg-white shadow-sm",
            action.active ? "left-[18px]" : "left-0.5"
          )}
        />
      </span>
    ) : action.trailingChevron || action.secondaryPanel ? (
      <ChevronRight className="ml-auto size-4 shrink-0 text-muted-foreground" aria-hidden />
    ) : null;

    return (
      <button
        key={action.id ?? `${keyPrefix}-${action.label}-${index}`}
        type="button"
        role={isToggle ? "menuitemcheckbox" : "menuitem"}
        aria-checked={isToggle ? action.active : undefined}
        // The label and description are separate inline spans, so the computed
        // name would run them together ("PlanRefine instructions").
        aria-label={action.description ? `${action.label}. ${action.description}` : undefined}
        tabIndex={-1}
        disabled={action.disabled}
        aria-haspopup={action.secondaryPanel ? "dialog" : undefined}
        aria-expanded={action.secondaryPanel ? action.secondaryPanel.open : undefined}
        onClick={(event) => {
          if (action.closeOnSelect === false) {
            event.currentTarget.focus();
          }
          handleMenuActionSelect(action);
        }}
        className={cn(
          "flex w-full cursor-pointer items-center gap-2 rounded-md border-0 bg-transparent px-2 text-left text-xs font-medium text-foreground hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring-focus disabled:cursor-not-allowed disabled:opacity-50",
          isToggle ? "h-auto py-1.5" : "h-8 whitespace-nowrap"
        )}
      >
        <span
          className={cn(
            "flex size-4 shrink-0 items-center justify-center [&>svg]:size-4",
            isToggle && "self-start"
          )}
        >
          {action.icon}
        </span>
        <span className={cn("flex min-w-0 flex-1", isToggle ? "flex-col" : "items-center gap-1")}>
          <span className={cn(!isToggle && "shrink-0")}>{action.label}</span>
          {action.description && (
            <span
              className={cn("font-normal text-muted-foreground", !isToggle && "min-w-0 truncate")}
            >
              {action.description}
            </span>
          )}
        </span>
        {trailing}
      </button>
    );
  };

  return (
    <UserFileSelector
      selectedFileIds={selectedFileIds}
      onSelectedFileIdsChange={onSelectedFileIdsChange}
      disabled={disabled}
      open={fileSelectorOpen}
      onOpenChange={handleFileSelectorOpenChange}
      refreshOnOpen={refreshYourFilesOnOpen}
      onYourFilesAccessChange={handleYourFilesAccessChange}
      renderTrigger={({ triggerRef }) => (
        <div ref={menuRootRef} className="relative">
          <Button
            ref={(node) => {
              triggerButtonRef.current = node;
              if (typeof triggerRef === "function") {
                triggerRef(node);
              } else if (triggerRef) {
                (triggerRef as React.MutableRefObject<HTMLButtonElement | null>).current = node;
              }
            }}
            type="button"
            variant="outline"
            size="icon"
            disabled={disabled}
            aria-label="Add attachment"
            aria-haspopup="menu"
            aria-expanded={menuOpen || fileSelectorOpen || secondaryPanelOpen}
            onClick={() => {
              if (secondaryPanelOpen) {
                menuActions.forEach((action) => {
                  if (action.secondaryPanel?.open) {
                    action.secondaryPanel.onOpenChange(false);
                  }
                });
                return;
              }
              if (fileSelectorOpen) {
                setFileSelectorOpen(false);
                return;
              }
              setMenuOpen(!menuOpen);
            }}
            className={cn(
              "h-9 w-9 shrink-0 cursor-pointer rounded-md bg-background hover:border-primary hover:bg-primary/5 hover:text-foreground",
              (menuOpen || fileSelectorOpen || secondaryPanelOpen || selectedFileIds.length > 0) &&
                "border-primary bg-accent text-primary"
            )}
          >
            <Plus className="h-4 w-4" aria-hidden />
          </Button>
          {uploadEnabled ? (
            <input
              ref={fileInputRef}
              type="file"
              multiple
              aria-label="Upload files"
              tabIndex={-1}
              disabled={disabled || yourFilesAccessDenied}
              className="sr-only"
              onChange={(event) => {
                const chosen = Array.from(event.target.files ?? []);
                event.target.value = "";
                const rejected = unsupportedChosenFiles(chosen);
                addRejectedFiles(rejected);
                progress.addUploads(chosen.filter((file) => !rejected.includes(file)));
              }}
            />
          ) : null}

          {menuOpen && (
            <div
              ref={menuRef}
              role="menu"
              aria-label="Attachment options"
              onKeyDown={handleMenuKeyDown}
              onBlur={handleMenuBlur}
              className={cn(
                "absolute bottom-full left-0 z-50 mb-2 rounded-lg border border-border bg-background p-1 text-foreground shadow-lg",
                hasToggleAction
                  ? "w-[min(21rem,calc(100vw-1.5rem))]"
                  : "min-w-[168px] max-w-[min(20rem,calc(100vw-1.5rem))]"
              )}
            >
              {topActions.length > 0 && (
                <>
                  <div role="group" aria-label="Top">
                    {topActions.map((action, index) => renderMenuAction(action, "top", index))}
                  </div>
                  <div role="separator" className="my-1 h-px bg-border" />
                </>
              )}
              {middleActions.map((action, index) => renderMenuAction(action, "middle", index))}
              {middleActions.length > 0 && <div role="separator" className="my-1 h-px bg-border" />}
              {uploadEnabled ? renderMenuAction(uploadFilesAction, "builtin", 0) : null}
              {renderMenuAction(openYourFilesAction, "builtin", 1)}
              {bottomActions.map((action, index) => renderMenuAction(action, "bottom", index))}
            </div>
          )}
          {menuActions.map((action, index) => {
            const secondaryPanel = action.secondaryPanel;
            if (!secondaryPanel?.open) return null;
            return (
              <React.Fragment key={action.id ?? `${action.label}-${index}`}>
                {secondaryPanel.render({
                  open: secondaryPanel.open,
                  anchorRef: menuRootRef,
                  triggerRef: triggerButtonRef,
                  onOpenChange: (nextOpen, options) => {
                    if (!nextOpen && options?.restoreFocus === false) {
                      suppressSecondaryFocusRestoreRef.current = true;
                    }
                    secondaryPanel.onOpenChange(nextOpen);
                    if (!nextOpen && options?.reopenMenu) {
                      setMenuOpen(true);
                    }
                  },
                  restoreTriggerFocus,
                })}
              </React.Fragment>
            );
          })}
        </div>
      )}
    />
  );
};
